import { httpsCallable } from 'firebase/functions';
import { functions } from '@/lib/firebase/config';

export interface TranslationResult {
  titleAr: string;
  titleEn: string;
  excerptAr: string;
  excerptEn: string;
  contentAr: string;
  contentEn: string;
}

export class ArticleTranslationService {
  private static instance: ArticleTranslationService;
  
  private constructor() {}
  
  static getInstance(): ArticleTranslationService {
    if (!ArticleTranslationService.instance) {
      ArticleTranslationService.instance = new ArticleTranslationService();
    }
    return ArticleTranslationService.instance;
  }

  /**
   * Translate article content from Arabic to English
   */
  async translateToEnglish(
    titleAr: string,
    excerptAr: string,
    contentAr: string
  ): Promise<{ titleEn: string; excerptEn: string; contentEn: string }> {
    const prompt = `You are a professional translator specializing in Arabic to English translation for a Saudi health and wellness organization called "Namat".

Translate the following article content from Arabic to English. Maintain the professional tone, cultural context, and meaning. The translation should be natural and fluent in English.

TITLE (Arabic):
${titleAr}

EXCERPT (Arabic):
${excerptAr}

CONTENT (Arabic):
${contentAr}

Respond with JSON only in this exact format:
{
  "titleEn": "English title translation",
  "excerptEn": "English excerpt translation",
  "contentEn": "English content translation"
}`;

    try {
      const aiChat = httpsCallable<{ system: string; prompt: string; temperature?: number; maxTokens?: number }, { content: string }>(functions, 'aiChat');
      const result = await aiChat({
        system: 'You are a professional Arabic to English translator specializing in health and wellness content for Saudi audiences. Always respond with valid JSON only. Maintain cultural sensitivity and professional tone.',
        prompt,
        temperature: 0.3, // Lower temperature for more consistent translations
        maxTokens: 3000,
      });
      const content = result.data.content?.trim();

      if (content) {
        try {
          const parsed = JSON.parse(content);
          return {
            titleEn: parsed.titleEn || '',
            excerptEn: parsed.excerptEn || '',
            contentEn: parsed.contentEn || '',
          };
        } catch (parseError) {
          console.error('Error parsing AI response:', parseError);
          throw new Error('Invalid JSON response from AI');
        }
      }

      throw new Error('Empty response from AI');
    } catch (error) {
      console.error('Error translating to English:', error);
      throw error;
    }
  }

  /**
   * Translate article content from English to Arabic
   */
  async translateToArabic(
    titleEn: string,
    excerptEn: string,
    contentEn: string
  ): Promise<{ titleAr: string; excerptAr: string; contentAr: string }> {
    const prompt = `You are a professional translator specializing in English to Arabic translation for a Saudi health and wellness organization called "نمط" (Namat).

Translate the following article content from English to Arabic. Maintain the professional tone, cultural context, and meaning. The translation should be natural, fluent, and culturally appropriate for Saudi audiences. Use Modern Standard Arabic (MSA).

TITLE (English):
${titleEn}

EXCERPT (English):
${excerptEn}

CONTENT (English):
${contentEn}

Respond with JSON only in this exact format:
{
  "titleAr": "الترجمة العربية للعنوان",
  "excerptAr": "الترجمة العربية للمقدمة",
  "contentAr": "الترجمة العربية للمحتوى"
}`;

    try {
      const aiChat = httpsCallable<{ system: string; prompt: string; temperature?: number; maxTokens?: number }, { content: string }>(functions, 'aiChat');
      const result = await aiChat({
        system: 'You are a professional English to Arabic translator specializing in health and wellness content for Saudi audiences. Always respond with valid JSON only. Use Modern Standard Arabic and maintain cultural sensitivity.',
        prompt,
        temperature: 0.3, // Lower temperature for more consistent translations
        maxTokens: 3000,
      });
      const content = result.data.content?.trim();

      if (content) {
        try {
          const parsed = JSON.parse(content);
          return {
            titleAr: parsed.titleAr || '',
            excerptAr: parsed.excerptAr || '',
            contentAr: parsed.contentAr || '',
          };
        } catch (parseError) {
          console.error('Error parsing AI response:', parseError);
          throw new Error('Invalid JSON response from AI');
        }
      }

      throw new Error('Empty response from AI');
    } catch (error) {
      console.error('Error translating to Arabic:', error);
      throw error;
    }
  }
}

export const articleTranslationService = ArticleTranslationService.getInstance();

